import React, { useState, useRef, useEffect } from 'react';
import MuxUploader from '@mux/mux-uploader-react';
import api from '../services/api';
import { 
  Video, X, Star, Loader2, AlertCircle, CheckCircle,
  Circle, StopCircle, RotateCcw
} from 'lucide-react';

const VideoReviewRecorder = ({ contentId, onClose, onSuccess }) => {
  const [step, setStep] = useState('rating'); // rating, permission, recording, preview, uploading, processing, complete, error
  const [starRating, setStarRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [error, setError] = useState(null);
  const [stream, setStream] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadUrl, setUploadUrl] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const MIN_DURATION = 10;
  const MAX_DURATION = 30;

  useEffect(() => {
    return () => {
      // Cleanup
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [stream]);

  const requestCameraPermission = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 720, height: 720 },
        audio: true
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setStep('recording');
    } catch (err) {
      console.error('Camera access error:', err);
      setError('Camera access denied. Please allow camera and microphone access to record a video review.');
      setStep('error');
    }
  };

  const startRecording = () => {
    chunksRef.current = [];
    const mediaRecorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
    mediaRecorderRef.current = mediaRecorder;

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      setRecordedBlob(blob);
      
      // Stop camera
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      
      // Show preview
      if (previewRef.current) {
        previewRef.current.src = URL.createObjectURL(blob);
      }
      setStep('preview');
    };

    mediaRecorder.start(100);
    setRecordingTime(0);
    
    // Start timer
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        const newTime = prev + 1;
        if (newTime >= MAX_DURATION) {
          stopRecording();
        }
        return newTime;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const retakeVideo = async () => {
    setRecordedBlob(null);
    setRecordingTime(0);
    setError(null);
    await requestCameraPermission();
  };

  const submitReview = async () => {
    if (recordingTime < MIN_DURATION) {
      setError(`Video must be at least ${MIN_DURATION} seconds long`);
      return;
    }

    try {
      setStep('uploading');
      
      // Create upload session
      const response = await api.post(`/video-reviews/upload/${contentId}?star_rating=${starRating}`);
      setUploadUrl(response.data.upload_url);
      setReviewId(response.data.review_id);
      
    } catch (err) {
      console.error('Failed to create upload:', err);
      setError(err.response?.data?.detail || 'Failed to create upload session');
      setStep('error');
    }
  };

  const handleUploadSuccess = async () => {
    setStep('processing');
    
    // Poll for status
    const checkStatus = async () => {
      try {
        const response = await api.get(`/video-reviews/status/${reviewId}`);
        if (response.data.status === 'ready') {
          setStep('complete');
          setTimeout(() => {
            if (onSuccess) onSuccess();
          }, 1500);
        } else if (response.data.status === 'failed') {
          setError(response.data.error || 'Video processing failed');
          setStep('error');
        } else {
          setTimeout(checkStatus, 2000);
        }
      } catch (err) {
        setTimeout(checkStatus, 2000);
      }
    };
    
    checkStatus();
  };

  const renderStarRating = () => (
    <div className="text-center space-y-6 py-4">
      <div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Rate this title</h3>
        <p className="text-foreground/60 text-sm">Select your rating before recording your review</p>
      </div>
      
      <div className="flex justify-center space-x-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => setStarRating(star)}
            onMouseEnter={() => setHoverRating(star)}
            onMouseLeave={() => setHoverRating(0)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star 
              className={`h-10 w-10 transition-colors ${
                star <= (hoverRating || starRating)
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-white/30'
              }`}
            />
          </button>
        ))}
      </div>
      
      {starRating > 0 && (
        <button
          onClick={() => setStep('permission')}
          className="px-8 py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-colors"
        >
          Continue to Record
        </button>
      )}
    </div>
  );

  const renderPermission = () => (
    <div className="text-center space-y-6 py-8">
      <div className="w-20 h-20 rounded-full bg-electric/20 flex items-center justify-center mx-auto">
        <Video className="h-10 w-10 text-electric" />
      </div>
      <div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Camera Access Required</h3>
        <p className="text-foreground/60">We need access to your camera and microphone to record your video review.</p>
      </div>
      <button
        onClick={requestCameraPermission}
        className="px-8 py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-colors"
      >
        Allow Camera Access
      </button>
    </div>
  );

  const renderRecording = () => (
    <div className="space-y-4">
      <div className="relative aspect-square max-w-sm mx-auto rounded-2xl overflow-hidden bg-black">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="w-full h-full object-cover mirror"
          style={{ transform: 'scaleX(-1)' }}
        />
        
        {/* Recording indicator */}
        {mediaRecorderRef.current?.state === 'recording' && (
          <div className="absolute top-4 left-4 flex items-center space-x-2 px-3 py-1 rounded-full bg-red-500/80">
            <Circle className="h-3 w-3 fill-white text-white animate-pulse" />
            <span className="text-white text-sm font-medium">REC</span>
          </div>
        )}
        
        {/* Timer */}
        <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-black/60">
          <span className={`text-sm font-mono ${recordingTime >= MIN_DURATION ? 'text-green-400' : 'text-white'}`}>
            {String(Math.floor(recordingTime / 60)).padStart(2, '0')}:{String(recordingTime % 60).padStart(2, '0')}
          </span>
        </div>
        
        {/* Duration guide */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full bg-black/60">
          <span className="text-xs text-white/80">
            {recordingTime < MIN_DURATION 
              ? `Min ${MIN_DURATION}s` 
              : `${MAX_DURATION - recordingTime}s remaining`
            }
          </span>
        </div>
      </div>
      
      <div className="flex justify-center space-x-4">
        {mediaRecorderRef.current?.state !== 'recording' ? (
          <button
            onClick={startRecording}
            className="flex items-center space-x-2 px-6 py-3 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors"
          >
            <Circle className="h-5 w-5 fill-white" />
            <span>Start Recording</span>
          </button>
        ) : (
          <button
            onClick={stopRecording}
            disabled={recordingTime < MIN_DURATION}
            className="flex items-center space-x-2 px-6 py-3 rounded-xl bg-white text-black font-semibold hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <StopCircle className="h-5 w-5" />
            <span>Stop Recording</span>
          </button>
        )}
      </div>
      
      <p className="text-center text-xs text-foreground/40">
        Record {MIN_DURATION}-{MAX_DURATION} seconds. Look at the camera and share your thoughts!
      </p>
    </div>
  );

  const renderPreview = () => (
    <div className="space-y-4">
      <div className="relative aspect-square max-w-sm mx-auto rounded-2xl overflow-hidden bg-black">
        <video
          ref={previewRef}
          controls
          playsInline
          className="w-full h-full object-cover"
          style={{ transform: 'scaleX(-1)' }}
        />
      </div>
      
      <div className="flex justify-center items-center space-x-2 text-foreground/60">
        <span>Your rating:</span>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star}
            className={`h-5 w-5 ${star <= starRating ? 'text-yellow-400 fill-yellow-400' : 'text-white/30'}`}
          />
        ))}
      </div>
      
      <div className="flex justify-center space-x-4">
        <button
          onClick={retakeVideo}
          className="flex items-center space-x-2 px-6 py-3 rounded-xl bg-white/10 text-foreground font-medium hover:bg-white/20 transition-colors"
        >
          <RotateCcw className="h-5 w-5" />
          <span>Retake</span>
        </button>
        <button
          onClick={submitReview}
          className="flex items-center space-x-2 px-6 py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-colors"
        >
          <CheckCircle className="h-5 w-5" />
          <span>Submit Review</span>
        </button>
      </div>
    </div>
  );

  const renderUploading = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-foreground mb-2">Uploading Review</h3>
      </div>
      
      {uploadUrl && recordedBlob && (
        <div className="bg-white/5 border border-white/20 rounded-xl p-4">
          <MuxUploader
            endpoint={uploadUrl}
            onSuccess={handleUploadSuccess}
            onError={(e) => {
              setError('Upload failed. Please try again.');
              setStep('error');
            }}
            style={{
              '--button-background-color': '#00F5D4',
              '--progress-bar-fill-color': '#00F5D4'
            }}
          />
        </div>
      )}
    </div>
  );

  const renderProcessing = () => (
    <div className="text-center space-y-6 py-8">
      <Loader2 className="h-12 w-12 text-electric animate-spin mx-auto" />
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Processing Your Review</h3>
        <p className="text-foreground/60 text-sm">This usually takes a few seconds...</p>
      </div>
    </div>
  );

  const renderComplete = () => (
    <div className="text-center space-y-6 py-8">
      <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
        <CheckCircle className="h-10 w-10 text-green-400" />
      </div>
      <div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Review Posted!</h3>
        <p className="text-foreground/60">Your video review is now live.</p>
      </div>
    </div>
  );

  const renderError = () => (
    <div className="text-center space-y-6 py-8">
      <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
        <AlertCircle className="h-10 w-10 text-red-400" />
      </div>
      <div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Something went wrong</h3>
        <p className="text-red-400">{error}</p>
      </div>
      <button
        onClick={() => {
          setError(null);
          setStep('rating');
        }}
        className="px-8 py-3 rounded-xl bg-electric text-background font-semibold hover:bg-electric/90 transition-colors"
      >
        Try Again
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-white/10 rounded-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg font-bold text-foreground">Video Review</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <X className="h-5 w-5 text-foreground/60" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {step === 'rating' && renderStarRating()}
          {step === 'permission' && renderPermission()}
          {step === 'recording' && renderRecording()}
          {step === 'preview' && renderPreview()}
          {step === 'uploading' && renderUploading()}
          {step === 'processing' && renderProcessing()}
          {step === 'complete' && renderComplete()}
          {step === 'error' && renderError()}
        </div>
      </div>
    </div>
  );
};

export default VideoReviewRecorder;
